/**
 * Sisters Beauty Center - Custom JavaScript
 *
 * Includes:
 * - Lenis smooth scroll
 * - GSAP + ScrollTrigger animations
 * - Treatwell booking integration
 * - Custom analytics
 */

(function() {
  'use strict';

  // ==========================================================================
  // Configuration
  // ==========================================================================
  const CONFIG = {
    treatwell: {
      bookingUrl: 'https://www.treatwell.it/salone/sisters-beauty-center/',
      showFloatingButton: true,
    },
    analytics: {
      enabled: true,
    },
    animations: {
      enabled: true,
      duration: 0.8,
      stagger: 0.15,
      ease: 'power3.out',
    }
  };

  // ==========================================================================
  // Lenis Smooth Scroll
  // ==========================================================================
  const SmoothScroll = {
    lenis: null,

    init: function() {
      if (typeof Lenis === 'undefined') {
        console.warn('Lenis not loaded');
        return;
      }

      this.lenis = new Lenis({
        duration: 1.2,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        orientation: 'vertical',
        gestureOrientation: 'vertical',
        smoothWheel: true,
        wheelMultiplier: 1,
        touchMultiplier: 2,
      });

      // Connect Lenis to GSAP ScrollTrigger
      if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
        this.lenis.on('scroll', ScrollTrigger.update);

        gsap.ticker.add((time) => {
          this.lenis.raf(time * 1000);
        });

        gsap.ticker.lagSmoothing(0);
      }

      // Fallback RAF loop if GSAP not available
      if (typeof gsap === 'undefined') {
        const raf = (time) => {
          this.lenis.raf(time);
          requestAnimationFrame(raf);
        };
        requestAnimationFrame(raf);
      }
    },

    scrollTo: function(target, options = {}) {
      if (this.lenis) {
        this.lenis.scrollTo(target, options);
      }
    }
  };

  // ==========================================================================
  // GSAP Animations
  // ==========================================================================
  const Animations = {
    init: function() {
      if (!CONFIG.animations.enabled) return;
      if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
        console.warn('GSAP or ScrollTrigger not loaded');
        return;
      }

      gsap.registerPlugin(ScrollTrigger);

      // Set defaults
      gsap.defaults({
        duration: CONFIG.animations.duration,
        ease: CONFIG.animations.ease,
      });

      // Initialize all animations
      this.animateHero();
      this.animateSections();
      this.animateCards();
      this.animateImages();
      this.animateButtons();
      this.animateText();
    },

    // Hero section - immediate elegant entrance
    animateHero: function() {
      const hero = document.querySelector('.slideshow, .hero, [class*="hero"], [class*="slideshow"]');
      if (!hero) return;

      const tl = gsap.timeline();

      // Hero content fade in
      tl.fromTo(hero.querySelectorAll('h1, h2, .hero__title, .slideshow__title'),
        { opacity: 0, y: 40 },
        { opacity: 1, y: 0, duration: 1, ease: 'power2.out' }
      )
      .fromTo(hero.querySelectorAll('p, .hero__subtitle, .slideshow__text'),
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.8 },
        '-=0.6'
      )
      .fromTo(hero.querySelectorAll('a, button, .btn, .button'),
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6 },
        '-=0.4'
      );
    },

    // General sections - fade up on scroll
    animateSections: function() {
      const sections = document.querySelectorAll(
        'section, .shopify-section, [class*="section"]'
      );

      sections.forEach((section, index) => {
        // Skip hero
        if (section.classList.contains('slideshow') ||
            section.classList.contains('hero') ||
            section.querySelector('.slideshow, .hero')) {
          return;
        }

        // Section header animation
        const headers = section.querySelectorAll('h2, h3, .section__title, .section-title');
        if (headers.length) {
          gsap.fromTo(headers,
            { opacity: 0, y: 50 },
            {
              opacity: 1,
              y: 0,
              duration: 0.8,
              scrollTrigger: {
                trigger: section,
                start: 'top 80%',
                toggleActions: 'play none none none',
              }
            }
          );
        }

        // Section subtitle/description
        const subtitles = section.querySelectorAll('.section__subtitle, .section-subtitle, .section__description');
        if (subtitles.length) {
          gsap.fromTo(subtitles,
            { opacity: 0, y: 30 },
            {
              opacity: 1,
              y: 0,
              duration: 0.6,
              delay: 0.2,
              scrollTrigger: {
                trigger: section,
                start: 'top 80%',
                toggleActions: 'play none none none',
              }
            }
          );
        }
      });
    },

    // Product/Team cards - fast staggered entrance
    animateCards: function() {
      const cardContainers = document.querySelectorAll(
        '.product-grid, .collection-grid, .team-grid'
      );

      cardContainers.forEach((container) => {
        const cards = container.querySelectorAll(
          '.product-card, .team-member, .grid__item'
        );

        if (cards.length === 0) return;

        gsap.fromTo(cards,
          {
            opacity: 0,
            y: 20,
          },
          {
            opacity: 1,
            y: 0,
            duration: 0.4,
            stagger: 0.04,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: container,
              start: 'top 90%',
              toggleActions: 'play none none none',
            }
          }
        );
      });
    },

    // Images - scale and fade
    animateImages: function() {
      const images = document.querySelectorAll(
        '.sisters-image-reveal, [class*="image-container"], .media-wrapper'
      );

      images.forEach((img) => {
        gsap.fromTo(img,
          {
            opacity: 0,
            scale: 1.1,
            clipPath: 'inset(10% 10% 10% 10%)'
          },
          {
            opacity: 1,
            scale: 1,
            clipPath: 'inset(0% 0% 0% 0%)',
            duration: 1,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: img,
              start: 'top 85%',
              toggleActions: 'play none none none',
            }
          }
        );
      });
    },

    // Buttons hover effect enhancement
    animateButtons: function() {
      const buttons = document.querySelectorAll('.sisters-btn, .btn, .button, [class*="button"]');

      buttons.forEach((btn) => {
        btn.addEventListener('mouseenter', function() {
          gsap.to(this, {
            scale: 1.02,
            duration: 0.3,
            ease: 'power2.out'
          });
        });

        btn.addEventListener('mouseleave', function() {
          gsap.to(this, {
            scale: 1,
            duration: 0.3,
            ease: 'power2.out'
          });
        });
      });
    },

    // Split text animation for headings
    animateText: function() {
      const animatedTexts = document.querySelectorAll('.sisters-text-reveal');

      animatedTexts.forEach((text) => {
        gsap.fromTo(text,
          {
            opacity: 0,
            y: 100,
            rotationX: -80
          },
          {
            opacity: 1,
            y: 0,
            rotationX: 0,
            duration: 1,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: text,
              start: 'top 90%',
              toggleActions: 'play none none none',
            }
          }
        );
      });
    }
  };

  // ==========================================================================
  // Parallax Effects
  // ==========================================================================
  const Parallax = {
    init: function() {
      if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;

      // Parallax images
      const parallaxElements = document.querySelectorAll('.sisters-parallax, [data-parallax]');

      parallaxElements.forEach((el) => {
        const speed = el.dataset.parallaxSpeed || 0.3;

        gsap.to(el, {
          yPercent: -30 * speed,
          ease: 'none',
          scrollTrigger: {
            trigger: el,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true,
          }
        });
      });
    }
  };

  // ==========================================================================
  // Magnetic Buttons (ConceptLashes style)
  // ==========================================================================
  const MagneticButtons = {
    init: function() {
      const magneticElements = document.querySelectorAll('.sisters-magnetic, [data-magnetic]');

      magneticElements.forEach((el) => {
        el.addEventListener('mousemove', (e) => {
          const rect = el.getBoundingClientRect();
          const x = e.clientX - rect.left - rect.width / 2;
          const y = e.clientY - rect.top - rect.height / 2;

          gsap.to(el, {
            x: x * 0.3,
            y: y * 0.3,
            duration: 0.3,
            ease: 'power2.out'
          });
        });

        el.addEventListener('mouseleave', () => {
          gsap.to(el, {
            x: 0,
            y: 0,
            duration: 0.5,
            ease: 'elastic.out(1, 0.5)'
          });
        });
      });
    }
  };

  // ==========================================================================
  // Treatwell Integration
  // ==========================================================================
  const Treatwell = {
    init: function() {
      document.querySelectorAll('[data-treatwell-booking]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.openBooking();
        });
      });

      if (CONFIG.treatwell.showFloatingButton) {
        this.createFloatingButton();
      }
    },

    openBooking: function() {
      if (CONFIG.analytics.enabled && typeof gtag === 'function') {
        gtag('event', 'booking_click', {
          'event_category': 'engagement',
          'event_label': 'treatwell_booking'
        });
      }
      window.open(CONFIG.treatwell.bookingUrl, '_blank');
    },

    createFloatingButton: function() {
      const excludedPages = ['/cart', '/checkout'];
      if (excludedPages.some(page => window.location.pathname.includes(page))) {
        return;
      }

      const button = document.createElement('a');
      button.href = CONFIG.treatwell.bookingUrl;
      button.target = '_blank';
      button.className = 'sisters-floating-booking';
      button.innerHTML = `
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
          <line x1="16" y1="2" x2="16" y2="6"></line>
          <line x1="8" y1="2" x2="8" y2="6"></line>
          <line x1="3" y1="10" x2="21" y2="10"></line>
        </svg>
        <span>Prenota</span>
      `;

      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.openBooking();
      });

      document.body.appendChild(button);

      // Animate in after page load
      if (typeof gsap !== 'undefined') {
        gsap.fromTo(button,
          { opacity: 0, x: 100 },
          { opacity: 1, x: 0, duration: 0.6, delay: 1.5, ease: 'power2.out' }
        );
      }
    }
  };

  // ==========================================================================
  // Custom Analytics
  // ==========================================================================
  const Analytics = {
    init: function() {
      if (!CONFIG.analytics.enabled) return;
      this.trackAddToCart();
    },

    trackAddToCart: function() {
      document.querySelectorAll('form[action="/cart/add"]').forEach((form) => {
        form.addEventListener('submit', () => {
          if (typeof gtag === 'function') {
            gtag('event', 'add_to_cart', {
              'event_category': 'ecommerce'
            });
          }
        });
      });
    }
  };

  // ==========================================================================
  // Cursor Effect (optional - ConceptLashes inspired)
  // ==========================================================================
  const CustomCursor = {
    cursor: null,
    follower: null,

    init: function() {
      // Only on desktop
      if (window.matchMedia('(pointer: coarse)').matches) return;

      this.createCursor();
      this.bindEvents();
    },

    createCursor: function() {
      this.cursor = document.createElement('div');
      this.cursor.className = 'sisters-cursor';

      this.follower = document.createElement('div');
      this.follower.className = 'sisters-cursor-follower';

      document.body.appendChild(this.cursor);
      document.body.appendChild(this.follower);
    },

    bindEvents: function() {
      document.addEventListener('mousemove', (e) => {
        gsap.to(this.cursor, {
          x: e.clientX,
          y: e.clientY,
          duration: 0.1
        });

        gsap.to(this.follower, {
          x: e.clientX,
          y: e.clientY,
          duration: 0.3
        });
      });

      // Scale on hover interactive elements
      const interactives = document.querySelectorAll('a, button, [role="button"], input, textarea');

      interactives.forEach((el) => {
        el.addEventListener('mouseenter', () => {
          this.cursor.classList.add('sisters-cursor--active');
          this.follower.classList.add('sisters-cursor-follower--active');
        });

        el.addEventListener('mouseleave', () => {
          this.cursor.classList.remove('sisters-cursor--active');
          this.follower.classList.remove('sisters-cursor-follower--active');
        });
      });
    }
  };

  // ==========================================================================
  // Header Scroll Effect
  // ==========================================================================
  const HeaderScroll = {
    header: null,
    scrollThreshold: 50,

    init: function() {
      // More specific selector for Shopify themes
      this.header = document.querySelector('.shopify-section-header .header, header.header, .header');
      if (!this.header) {
        console.warn('Header element not found');
        return;
      }

      this.bindScroll();
      this.checkScroll(); // Check initial state
    },

    bindScroll: function() {
      let ticking = false;

      window.addEventListener('scroll', () => {
        if (!ticking) {
          window.requestAnimationFrame(() => {
            this.checkScroll();
            ticking = false;
          });
          ticking = true;
        }
      });
    },

    checkScroll: function() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

      if (scrollTop > this.scrollThreshold) {
        this.header.classList.add('scrolled');
      } else {
        this.header.classList.remove('scrolled');
      }
    }
  };

  // ==========================================================================
  // Menu Drawer Scroll Lock
  // ==========================================================================
  const MenuDrawerLock = {
    scrollY: 0,

    init: function() {
      const details = document.getElementById('Details-menu-drawer-container');
      if (!details) return;

      const observer = new MutationObserver(() => {
        if (details.hasAttribute('open')) {
          this.scrollY = window.scrollY;
          document.body.classList.add('menu-drawer-open');
          document.body.style.top = '-' + this.scrollY + 'px';
        } else {
          document.body.classList.remove('menu-drawer-open');
          document.body.style.top = '';
          window.scrollTo(0, this.scrollY);
        }
      });

      observer.observe(details, { attributes: true, attributeFilter: ['open'] });
    }
  };

  // ==========================================================================
  // Initialize Everything
  // ==========================================================================
  function init() {
    // Core
    SmoothScroll.init();
    Animations.init();
    Parallax.init();
    MagneticButtons.init();
    HeaderScroll.init();

    // Features
    Treatwell.init();
    Analytics.init();
    MenuDrawerLock.init();

    // Optional: Custom cursor (uncomment to enable)
    // CustomCursor.init();

    console.log('Sisters Beauty Center - All systems loaded');
  }

  // Wait for DOM
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose to window for debugging
  window.SistersBeauty = {
    smoothScroll: SmoothScroll,
    animations: Animations,
    config: CONFIG
  };

})();
